import React, { useState, useRef } from "react";
import { X, Table, Cloud, Upload, Download } from "lucide-react";
import type { DataRow } from "./ChartDataTable";
import { ChartDataTable } from "./ChartDataTable";
import { fetchSheet } from "../../../../api/sheets";
import { getTimeEntrySheetId } from "../../../../constants/timekeeping";

interface ChartDataSourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  rows: DataRow[];
  series: string[];
  seriesColors: Record<string, string>;
  onRowsChange: (rows: DataRow[]) => void;
  onSeriesChange: (series: string[]) => void;
  onSeriesColorsChange: (colors: Record<string, string>) => void;
  onSave: () => void;
}

export function ChartDataSourceModal({
  isOpen,
  onClose,
  rows,
  series,
  seriesColors,
  onRowsChange,
  onSeriesChange,
  onSeriesColorsChange,
  onSave,
}: ChartDataSourceModalProps) {
  // AC-6.3.2: Detect current domain for correct data source
  const [selectedSheetId, setSelectedSheetId] = useState(() => {
    const domainId = localStorage.getItem("swm-current-workspace-id");
    return getTimeEntrySheetId(domainId);
  });

  const [isLoading, setIsLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(
    rows.length > 0 && rows[0].label !== ""
  );
  const [error, setError] = useState("");

  // Update selected sheet when modal opens or domain changes
  React.useEffect(() => {
    if (isOpen) {
      const domainId = localStorage.getItem("swm-current-workspace-id");
      setSelectedSheetId(getTimeEntrySheetId(domainId));
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleLoadData = async () => {
    setIsLoading(true);
    setError("");

    try {
      const sheet = await fetchSheet(selectedSheetId);

      if (!sheet.fields || !sheet.rows) {
        throw new Error("Sheet data is incomplete");
      }

      // Map field IDs to names for column headers
      const fieldIdToName = new Map<string, string>();
      const numericFields: string[] = [];

      sheet.fields.forEach((field) => {
        fieldIdToName.set(field.id, field.name);
        // Identify potential series (numeric fields)
        if (["number", "currency", "percent", "rating"].includes(field.type)) {
          numericFields.push(field.name);
        }
      });

      // Transform rows to DataRow format
      // We'll use the first text field found as the default "label" (or just "Name" or "Title")
      // If no appropriate label found, we might need to rely on the first column.

      const newRows: DataRow[] = sheet.rows.map((row) => {
        const dataRow: DataRow = { label: "" };

        // Find a suitable label. Prefer "name", "title", "date", or just the first text field.
        let labelFound = false;

        // Populate all columns by name
        sheet.fields.forEach((field) => {
          const value = row.data[field.id];
          const fieldName = field.name;

          // Convert value to string for DataRow
          let stringValue = "";
          if (value !== null && value !== undefined) {
            stringValue = String(value);
          }

          dataRow[fieldName] = stringValue;

          // Heuristic for Label: First NON-ID text field, or specific names
          if (
            !labelFound &&
            field.type === "text" &&
            field.name.toLowerCase() !== "id"
          ) {
            dataRow.label = stringValue; // Default label
            labelFound = true;
          }
        });

        // If still no label, use the first field's value
        if (!dataRow.label && sheet.fields.length > 0) {
          const firstFieldId = sheet.fields[0].id;
          dataRow.label = String(row.data[firstFieldId] || "");
        }

        return dataRow;
      });

      onRowsChange(newRows);

      // Update series if we found numeric fields
      if (numericFields.length > 0) {
        onSeriesChange(numericFields);

        // Reset colors for new series
        const defaultColors = [
          "#8b5cf6",
          "#3b82f6",
          "#10b981",
          "#ef4444",
          "#f97316",
          "#ec4899",
          "#14b8a6",
          "#f59e0b",
        ];
        const newColors: Record<string, string> = {};
        numericFields.forEach((s, i) => {
          newColors[s] = defaultColors[i % defaultColors.length];
        });
        onSeriesColorsChange(newColors);
      } else {
        // If no purely numeric fields, maybe default to some columns or let user pick?
        // For now, let's just make sure we capture all fields as potnetial columns in row data
        // but maybe clear series if none found
        onSeriesChange([]);
      }

      setDataLoaded(true);
    } catch (err: any) {
      console.error("Failed to load sheet data:", err);
      setError(err.message || "Failed to load sheet data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = () => {
    onSave();
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Chart Data Source
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-auto p-4">
          <div className="space-y-6">
            {/* Sheet Selection */}
            <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Data Source
              </label>
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <select
                    value={selectedSheetId}
                    onChange={(e) => setSelectedSheetId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                  >
                    <option value={selectedSheetId}>
                      Time Entries{" "}
                      {selectedSheetId.includes("-t-")
                        ? "(Current Domain)"
                        : "(Shared)"}
                    </option>
                    {/* Can add more sheets here in future */}
                  </select>
                </div>
                <button
                  onClick={handleLoadData}
                  disabled={isLoading}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Loading...
                    </>
                  ) : (
                    <>
                      <Table size={16} />
                      Load Data
                    </>
                  )}
                </button>
              </div>
              {error && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                  {error}
                </p>
              )}
            </div>

            {/* Data Preview / Selection */}
            {dataLoaded && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                    Data Preview & Selection
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Select columns to visualize in the chart configuration
                  </p>
                </div>

                <ChartDataTable
                  rows={rows}
                  series={series}
                  seriesColors={seriesColors}
                  onRowsChange={onRowsChange}
                  onSeriesChange={onSeriesChange}
                  onSeriesColorsChange={onSeriesColorsChange}
                />
              </div>
            )}

            {!dataLoaded && !isLoading && (
              <div className="text-center py-10 text-gray-500 dark:text-gray-400">
                <Table size={48} className="mx-auto mb-3 opacity-20" />
                <p>Load a data source to configure the chart</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg font-semibold transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!dataLoaded}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save Configuration
          </button>
        </div>
      </div>
    </div>
  );
}
